import { MANDATORY_LEGAL_DOCUMENTS } from '../MandatoryLegalDocuments';
import { LegalDocument } from '../model/LegalDocument';
import { LegalDocumentApproval } from '../model/LegalDocumentApproval';
import { LEGAL_DOCUMENTS_TYPES, LegalDocumentType } from '../model/LegalDocumentType';

import { LegalDocumentVersion } from './api/BaseLegalDocument';
import {
    ApprovedLegalDocument as ApprovedApiLegalDocument,
    LegalDocumentUserApproval,
} from './api/GetApprovedDocumentApiResult';
import { LegalDocument as ToApproveApiLegalDocument } from './api/GetToApproveDocumentApiResult';

function mapDocument(
    documentType: LegalDocumentType,
    version: LegalDocumentVersion,
    currentLanguage: string,
): LegalDocument {
    const translation = version.translations.find((t) => t.languageCode === currentLanguage) ?? version.translations[0];
    const lastRevision = translation.revisions[translation.revisions.length - 1];

    return {
        documentType,
        language: translation.languageCode,
        url: lastRevision.fileServerUrl,
    };
}

export function fromToApproveApiResponse(document: ToApproveApiLegalDocument, currentLanguage: string): LegalDocument {
    return mapDocument(document.documentType, document.latestApplicableVersion, currentLanguage);
}

export function fromApprovedApiResponse(document: ApprovedApiLegalDocument, currentLanguage: string): LegalDocument {
    return mapDocument(document.documentType, document.approvedVersion, currentLanguage);
}

/** The documents are translated into the language part of the locale ("fr" for "fr-FR"). */
export function toDocumentLanguage(locale: string): string {
    return locale.split('-')[0];
}

/** The last approval, with its known documents; null when the installer never signed anything. */
export function toLastApproval(approvals: LegalDocumentUserApproval[], language: string): LegalDocumentApproval | null {
    const lastApproval = approvals.at(-1);
    if (!lastApproval) return null;

    // One document per type: a type listed twice keeps its first position and its last version.
    const documents = new Map(
        lastApproval.legalDocuments
            .filter((document) => Object.values(LEGAL_DOCUMENTS_TYPES).includes(document.documentType))
            .map((document): [LegalDocumentType, LegalDocument] => [
                document.documentType,
                fromApprovedApiResponse(document, language),
            ]),
    );

    return { approvalDate: lastApproval.creationTime, approvedDocuments: [...documents.values()] };
}

/** The mandatory documents still to sign, in signing order: terms of use first, then data privacy. */
export function toDocumentsToSign(documents: ToApproveApiLegalDocument[], language: string): LegalDocument[] {
    return MANDATORY_LEGAL_DOCUMENTS.flatMap((type) =>
        documents.filter((document) => document.documentType === type),
    ).map((document) => fromToApproveApiResponse(document, language));
}
